import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    createIncome,
    createRecurringTransaction,
    deleteRecurringTransaction,
    getCustomers,
    getIncomeFile,
    getIncomeMonths,
    getIncomesForMonth,
    getRecurringTransactions,
    updateIncome,
    updateRecurringTransaction,
    uploadIncome,
} from "../../api";
import { logError } from "../../logging/loggingService";
import { Alert, Snackbar } from "@mui/material";
import { useAuth } from "../auth/AuthContext";
import { hasAccountingAccess } from "../../subscription";
import {
    buildCustomerOptions,
    resolveCustomerOption,
} from "../shared/customerSelectUtils";
import IncomeDetailsDialog from "./incomeManagement/IncomeDetailsDialog";
import IncomeManagementHeader from "./incomeManagement/IncomeManagementHeader";
import IncomeManagementUpgradeNotice from "./incomeManagement/IncomeManagementUpgradeNotice";
import IncomeMonthsList from "./incomeManagement/IncomeMonthsList";
import IncomeReceiptDialog from "./incomeManagement/IncomeReceiptDialog";
import IncomeUploadDialog from "./incomeManagement/IncomeUploadDialog";
import IncomeToolbar from "./incomeManagement/IncomeToolbar";
import RecurringIncomeDialog from "./incomeManagement/RecurringIncomeDialog";
import RecurringIncomeSection from "./incomeManagement/RecurringIncomeSection";
import {
    applyEuCalculation,
    buildMonthKey,
    emptyToNull,
    extOf,
    formatBytes,
    formatDate,
    formatDateOrDash,
    fmtEUR,
    getDefaultIncomeGroupState,
    incomeGroupStateKey,
    isFiniteNumber,
    normalizeIncomeMonths,
    parseDayOfMonth,
    parseDecimalInput,
    parseOrNull,
    readIncomeGroupState,
    recurrenceLabel,
    recurrenceOptions,
    resolveEuNetAmount,
    resolveIncomeDate,
    sortIncomesByDate,
    toDateInputValue,
    toEditableString,
    toNumOrEmpty,
} from "./incomeManagement/utils";

export default function IncomeManagement() {
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    const [incomeMonthIndex, setIncomeMonthIndex] = useState([]);
    const [incomeMonthIndexLoaded, setIncomeMonthIndexLoaded] = useState(false);
    const [incomeMonthData, setIncomeMonthData] = useState({});
    const [loadingIncomeMonths, setLoadingIncomeMonths] = useState(() => new Set());
    const [loadedIncomeMonths, setLoadedIncomeMonths] = useState(() => new Set());
    const [expandedIncomeGroups, setExpandedIncomeGroups] = useState(() => {
        const stored = readIncomeGroupState();
        return stored ?? getDefaultIncomeGroupState();
    });

    const [open, setOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [manual, setManual] = useState(false);
    const [incomeData, setIncomeData] = useState(null);
    const [saving, setSaving] = useState(false);

    const updateIncomeData = useCallback((patch, changedField) => {
        setIncomeData((prev) => {
            const base = prev || {
                taxExcludedContribution: false,
                isEuService: false,
                customerId: "",
                customerName: "",
            };
            return applyEuCalculation(base, patch, changedField, (value) => value);
        });
    }, []);

    const clearFieldError = (setter, field) => {
        setter((prev) => {
            if (!prev || !prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const clearIncomeError = (field) => clearFieldError(setIncomeErrors, field);
    const clearEditError = (field) => clearFieldError(setEditErrors, field);
    const clearRecurringError = (field) => clearFieldError(setRecurringErrors, field);

    const applyIncomeErrors = (details, setter) => {
        if (!Array.isArray(details) || details.length === 0) return false;
        const next = {};
        details.forEach((detail) => {
            const rawField = detail?.field;
            const message = detail?.message;
            if (!rawField || !message) return;
            const normalizedField = rawField.replace(/_([a-z])/g, (_, char) => char.toUpperCase());
            const field = normalizedField === "customerId" ? "customerName" : normalizedField;
            next[field] = message;
        });
        if (Object.keys(next).length === 0) return false;
        setter(next);
        return true;
    };

    const applyRecurringErrors = (details) => {
        if (!Array.isArray(details) || details.length === 0) return false;
        const next = {};
        details.forEach((detail) => {
            const rawField = detail?.field;
            const message = detail?.message;
            if (!rawField || !message) return;
            const normalizedField = rawField.replace(/_([a-z])/g, (_, char) => char.toUpperCase());
            const field =
                normalizedField === "customerName" || normalizedField === "customerId"
                    ? "counterparty"
                    : normalizedField;
            next[field] = message;
        });
        if (Object.keys(next).length === 0) return false;
        setRecurringErrors(next);
        return true;
    };

    const [query, setQuery] = useState("");
    const [sortBy, setSortBy] = useState("date-desc");

    const [toast, setToast] = useState({ open: false, msg: "", severity: "success" });
    const [incomeErrors, setIncomeErrors] = useState({});
    const [customers, setCustomers] = useState([]);
    const [loadingCustomers, setLoadingCustomers] = useState(true);

    // Details-/Bearbeiten-Dialog
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailIncome, setDetailIncome] = useState(null);     // Original
    const [editIncome, setEditIncome] = useState(null);         // Edit-Model
    const [detailSaving, setDetailSaving] = useState(false);
    const [editErrors, setEditErrors] = useState({});
    const [copyingId, setCopyingId] = useState(null);
    const [receiptOpen, setReceiptOpen] = useState(false);
    const [receiptIncome, setReceiptIncome] = useState(null);
    const [receiptPreview, setReceiptPreview] = useState(null);
    const [receiptLoading, setReceiptLoading] = useState(false);
    const [receiptError, setReceiptError] = useState(null);

    const [recurringIncomes, setRecurringIncomes] = useState([]);
    const [recurringLoading, setRecurringLoading] = useState(false);
    const [recurringError, setRecurringError] = useState(null);
    const [recurringDialogOpen, setRecurringDialogOpen] = useState(false);
    const [recurringForm, setRecurringForm] = useState(null);
    const [recurringMode, setRecurringMode] = useState("create");
    const [recurringSaving, setRecurringSaving] = useState(false);
    const [recurringErrors, setRecurringErrors] = useState({});
    const [recurringDeleteId, setRecurringDeleteId] = useState(null);

    const dropRef = useRef(null);
    const fileInputRef = useRef(null);

    const { user } = useAuth();
    const hasAccounting = hasAccountingAccess(user?.paymentPlan);

    const sortedCustomers = useMemo(() => {
        const list = Array.isArray(customers) ? [...customers] : [];
        return list.sort((a, b) => {
            const nameA = `${a.name || ""} ${a.lastname || ""}`.trim();
            const nameB = `${b.name || ""} ${b.lastname || ""}`.trim();
            return nameA.localeCompare(nameB, "de");
        });
    }, [customers]);

    const customerOptions = useMemo(
        () => buildCustomerOptions(sortedCustomers),
        [sortedCustomers]
    );

    const incomeCustomerOption = useMemo(
        () =>
            resolveCustomerOption(
                customerOptions,
                incomeData?.customerId,
                incomeData?.customerName
            ),
        [customerOptions, incomeData?.customerId, incomeData?.customerName]
    );

    const editCustomerOption = useMemo(
        () =>
            resolveCustomerOption(
                customerOptions,
                editIncome?.customerId,
                editIncome?.customerName
            ),
        [customerOptions, editIncome?.customerId, editIncome?.customerName]
    );

    const recurringCustomerOption = useMemo(
        () =>
            resolveCustomerOption(
                customerOptions,
                recurringForm?.customerId,
                recurringForm?.counterparty
            ),
        [customerOptions, recurringForm?.customerId, recurringForm?.counterparty]
    );

    const fileName = file?.name || "";
    const fileType = file?.type || "";
    const isPreviewPdf = Boolean(file) && (fileType === "application/pdf" || fileName.toLowerCase().endsWith(".pdf"));
    const isPreviewImage =
        Boolean(file) &&
        (fileType.startsWith("image/") || /\.(png|jpe?g|gif|bmp|webp)$/i.test(fileName));

    const ensureIncomeMonthIndex = useCallback((year, monthKey) => {
        const yearNum = Number(year);
        const monthNum = Number(monthKey);
        if (!Number.isFinite(yearNum) || !Number.isFinite(monthNum)) return;
        setIncomeMonthIndex((prev) => {
            const exists = prev.some((entry) => entry.year === yearNum && entry.month === monthNum);
            if (exists) return prev;
            return [...prev, { year: yearNum, month: monthNum }];
        });
    }, []);

    const loadIncomeMonthIndex = useCallback(async (options = {}) => {
        if (incomeMonthIndexLoaded && !options.force) return incomeMonthIndex;
        try {
            const res = await getIncomeMonths();
            const normalized = normalizeIncomeMonths(res?.data);
            setIncomeMonthIndex(normalized);
            return normalized;
        } catch (e) {
            logError("Fehler beim Laden der Einnahmenmonate", e, { component: "IncomeManagement" });
            setIncomeMonthIndex([]);
            return [];
        } finally {
            setIncomeMonthIndexLoaded(true);
        }
    }, [incomeMonthIndex, incomeMonthIndexLoaded]);

    const loadIncomesForMonthKey = useCallback(async (year, monthKey, options = {}) => {
        const yearNum = Number(year);
        const monthNum = Number(monthKey);
        if (!Number.isFinite(yearNum) || !Number.isFinite(monthNum)) return [];
        const key = buildMonthKey(String(yearNum), String(monthNum));
        if (loadingIncomeMonths.has(key)) return [];
        if (!options.force && loadedIncomeMonths.has(key)) return incomeMonthData[key] || [];

        setLoadingIncomeMonths((prev) => {
            const next = new Set(prev);
            next.add(key);
            return next;
        });

        try {
            const res = await getIncomesForMonth(yearNum, monthNum);
            const data = sortIncomesByDate(Array.isArray(res?.data) ? res.data : []);
            setIncomeMonthData((prev) => ({ ...prev, [key]: data }));
            setLoadedIncomeMonths((prev) => {
                const next = new Set(prev);
                next.add(key);
                return next;
            });
            ensureIncomeMonthIndex(yearNum, monthNum);
            return data;
        } catch (e) {
            logError("Fehler beim Laden der Einnahmen", e, { component: "IncomeManagement" });
            return [];
        } finally {
            setLoadingIncomeMonths((prev) => {
                const next = new Set(prev);
                next.delete(key);
                return next;
            });
        }
    }, [loadingIncomeMonths, loadedIncomeMonths, incomeMonthData, ensureIncomeMonthIndex]);

    const expandedGroupsRef = useRef(expandedIncomeGroups);
    useEffect(() => { expandedGroupsRef.current = expandedIncomeGroups; }, [expandedIncomeGroups]);

    const loadedMonthsRef = useRef(loadedIncomeMonths);
    useEffect(() => { loadedMonthsRef.current = loadedIncomeMonths; }, [loadedIncomeMonths]);

    const refresh = useCallback(async (options = {}) => {
        const { silent = false } = options;
        if (!silent) {
            setLoading(true);
        }
        setErr(null);
        try {
            await loadIncomeMonthIndex({ force: true });

            const expandedKeys = Object.entries(expandedGroupsRef.current?.months || {})
                .filter(([, isExpanded]) => isExpanded)
                .map(([key]) => key);

            const monthsToLoad = new Set([
                ...Array.from(loadedMonthsRef.current),
                ...expandedKeys,
            ]);

            const tasks = Array.from(monthsToLoad)
                .map((key) => {
                    const [year, monthKey] = key.split("-");
                    if (!Number.isFinite(Number(year)) || !Number.isFinite(Number(monthKey))) return null;
                    return loadIncomesForMonthKey(year, monthKey, { force: true });
                })
                .filter(Boolean);

            await Promise.all(tasks);
        } catch (e) {
            logError("Fehler beim Laden der Einnahmen", e, { component: "IncomeManagement" });
            setErr("Einnahmen konnten nicht geladen werden.");
        } finally {
            if (!silent) {
                setLoading(false);
            }
        }
    }, [loadIncomeMonthIndex, loadIncomesForMonthKey]);

    const toggleYearExpanded = (year) => {
        setExpandedIncomeGroups((prev) => {
            const years = { ...(prev?.years || {}) };
            years[year] = !years[year];
            return { years, months: prev?.months || {} };
        });
    };

    const toggleMonthExpanded = (year, monthKey) => {
        const key = buildMonthKey(year, monthKey);
        const isExpanded = !!expandedIncomeGroups?.months?.[key];
        setExpandedIncomeGroups((prev) => {
            const months = { ...(prev?.months || {}) };
            months[key] = !months[key];
            return { years: prev?.years || {}, months };
        });
        if (!isExpanded) {
            loadIncomesForMonthKey(year, monthKey);
        }
    };

    const fetchRecurringIncomes = useCallback(() => {
        setRecurringLoading(true);
        setRecurringError(null);
        return getRecurringTransactions("INCOME")
            .then((res) => setRecurringIncomes(Array.isArray(res.data) ? res.data : []))
            .catch((e) => {
                logError("Fehler beim Laden der wiederkehrenden Einnahmen", e, { component: "IncomeManagement" });
                setRecurringError("Wiederkehrende Einnahmen konnten nicht geladen werden.");
            })
            .finally(() => setRecurringLoading(false));
    }, []);

    useEffect(() => {
        if (!hasAccounting) {
            setLoading(false);
            setErr(null);
            setIncomeMonthIndex([]);
            setIncomeMonthIndexLoaded(false);
            setIncomeMonthData({});
            setLoadingIncomeMonths(new Set());
            setLoadedIncomeMonths(new Set());
            setCustomers([]);
            setLoadingCustomers(false);
            setRecurringLoading(false);
            setRecurringIncomes([]);
            setRecurringError(null);
            return;
        }
        setLoadingCustomers(true);
        getCustomers()
            .then((res) => setCustomers(res.data || []))
            .catch((err) => logError("Failed to fetch customers", err))
            .finally(() => setLoadingCustomers(false));
        refresh();
        fetchRecurringIncomes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasAccounting]);

    const resetUploadDialog = () => {
        setOpen(false);
        setFile(null);
        setManual(false);
        setIncomeData(null);
        setPreviewVisible(false);
        setPreviewUrl(null);
        setIncomeErrors({});
    };

    const handleCloseUpload = () => {
        if (uploading || saving) return;
        resetUploadDialog();
    };

    const handleOpenUpload = () => {
        setFile(null);
        setManual(false);
        setIncomeData(null);
        setPreviewVisible(false);
        setPreviewUrl(null);
        setIncomeErrors({});
        setOpen(true);
    };

    const handleManualToggle = (checked) => {
        setManual(checked);
        setIncomeErrors({});
        if (checked) {
            setIncomeData((prev) =>
                prev || {
                    taxExcludedContribution: false,
                    isEuService: false,
                    customerId: "",
                    customerName: "",
                }
            );
            setFile(null);
        } else {
            setIncomeData(null);
        }
    };

    const handleUpload = async () => {
        if (!file) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append("file", file);
            await uploadIncome(formData);
            await refresh({ silent: true });
            resetUploadDialog();
            setToast({ open: true, msg: "Einnahme erfolgreich hochgeladen.", severity: "success" });
        } catch (e) {
            logError("Fehler beim Hochladen der Einnahme", e, { component: "IncomeManagement" });
            setToast({ open: true, msg: "Upload fehlgeschlagen.", severity: "error" });
        } finally {
            setUploading(false);
        }
    };

    const handleSave = async () => {
        if (!incomeData) return;
        setIncomeErrors({});
        if (!validateEuService(incomeData, setIncomeErrors)) return;
        setSaving(true);
        try {
            const { customerId, ...payloadBase } = incomeData || {};
            const payload = {
                ...payloadBase,
                taxExcludedContribution: Boolean(incomeData?.taxExcludedContribution),
                isEuService: Boolean(incomeData?.isEuService),
            };
            await createIncome(payload);
            await refresh({ silent: true });
            resetUploadDialog();
            setToast({ open: true, msg: "Einnahme gespeichert.", severity: "success" });
        } catch (e) {
            logError("Fehler beim Speichern der Einnahme", e, { component: "IncomeManagement" });
            const backendError = e?.response?.data;
            const details = Array.isArray(backendError?.details) ? backendError.details : null;
            if (details) {
                applyIncomeErrors(details, setIncomeErrors);
            }
            setToast({ open: true, msg: "Speichern fehlgeschlagen.", severity: "error" });
        } finally {
            setSaving(false);
        }
    };

    const updateRecurringForm = (field, value) => {
        setRecurringForm((prev) => ({ ...(prev || {}), [field]: value }));
        clearRecurringError(field);
    };

    const handleOpenRecurringCreate = () => {
        const today = new Date();
        const start = toDateInputValue(today);
        setRecurringMode("create");
        setRecurringErrors({});
        setRecurringForm({
            id: null,
            title: "",
            invoiceNumberTemplate: "",
            counterparty: "",
            customerId: "",
            grossAmount: "",
            netAmount: "",
            vatAmount: "",
            recurrencePeriod: "MONTHLY",
            dayOfMonth: String(today.getDate()),
            startDate: start,
            endDate: "",
            nextExecutionDate: "",
        });
        setRecurringDialogOpen(true);
    };

    const handleOpenRecurringEdit = (transaction) => {
        if (!transaction) return;
        setRecurringMode("edit");
        setRecurringErrors({});
        setRecurringForm({
            id: transaction.id,
            title: transaction.title || "",
            invoiceNumberTemplate: transaction.invoiceNumberTemplate || "",
            counterparty: transaction.counterparty || "",
            customerId: transaction.customerId || "",
            grossAmount: toEditableString(transaction.grossAmount),
            netAmount: toEditableString(transaction.netAmount),
            vatAmount: toEditableString(transaction.vatAmount),
            recurrencePeriod: transaction.recurrencePeriod || "MONTHLY",
            dayOfMonth: toEditableString(transaction.dayOfMonth),
            startDate: toDateInputValue(transaction.startDate),
            endDate: toDateInputValue(transaction.endDate),
            nextExecutionDate: toDateInputValue(transaction.nextExecutionDate),
        });
        setRecurringDialogOpen(true);
    };

    const handleCloseRecurringDialog = () => {
        setRecurringDialogOpen(false);
        setRecurringForm(null);
        setRecurringMode("create");
        setRecurringSaving(false);
        setRecurringErrors({});
    };

    const handleSaveRecurring = async () => {
        if (!recurringForm) return;
        setRecurringErrors({});
        const payload = {
            id: recurringForm.id,
            transactionType: "INCOME",
            recurrencePeriod: recurringForm.recurrencePeriod || "MONTHLY",
            title: emptyToNull(recurringForm.title),
            invoiceNumberTemplate: emptyToNull(recurringForm.invoiceNumberTemplate),
            counterparty: emptyToNull(recurringForm.counterparty),
            grossAmount: parseDecimalInput(recurringForm.grossAmount),
            netAmount: parseDecimalInput(recurringForm.netAmount),
            vatAmount: parseDecimalInput(recurringForm.vatAmount),
            startDate: emptyToNull(recurringForm.startDate),
            endDate: emptyToNull(recurringForm.endDate),
            dayOfMonth: parseDayOfMonth(recurringForm.dayOfMonth),
            nextExecutionDate: emptyToNull(recurringForm.nextExecutionDate),
        };

        setRecurringSaving(true);
        try {
            if (recurringMode === "edit" && payload.id) {
                await updateRecurringTransaction(payload.id, payload);
                setToast({ open: true, msg: "Wiederkehrende Einnahme aktualisiert.", severity: "success" });
            } else {
                const { id: _id, ...createPayload } = payload;
                await createRecurringTransaction(createPayload);
                setToast({ open: true, msg: "Wiederkehrende Einnahme gespeichert.", severity: "success" });
            }
            await fetchRecurringIncomes();
            await refresh({ silent: true });
            handleCloseRecurringDialog();
        } catch (e) {
            logError("Fehler beim Speichern der wiederkehrenden Einnahme", e, { component: "IncomeManagement" });
            const backendError = e?.response?.data;
            const details = Array.isArray(backendError?.details) ? backendError.details : null;
            if (details) {
                applyRecurringErrors(details);
            }
            setToast({ open: true, msg: "Wiederkehrende Einnahme konnte nicht gespeichert werden.", severity: "error" });
        } finally {
            setRecurringSaving(false);
        }
    };

    const handleDeleteRecurring = async (transaction) => {
        if (!transaction?.id) return;
        const confirmed = window.confirm(
            `Wiederkehrende Einnahme "${transaction.title || "Vorlage"}" wirklich löschen?`
        );
        if (!confirmed) return;
        setRecurringDeleteId(transaction.id);
        try {
            await deleteRecurringTransaction(transaction.id);
            await fetchRecurringIncomes();
            await refresh({ silent: true });
            setToast({ open: true, msg: "Wiederkehrende Einnahme gelöscht.", severity: "success" });
        } catch (e) {
            logError("Fehler beim Löschen der wiederkehrenden Einnahme", e, { component: "IncomeManagement" });
            setToast({ open: true, msg: "Wiederkehrende Einnahme konnte nicht gelöscht werden.", severity: "error" });
        } finally {
            setRecurringDeleteId(null);
        }
    };

    const resolveIncomeCustomerName = useCallback((income) => {
        if (!income) return "";
        if (income.customer) {
            return `${income.customer.name || ""} ${income.customer.lastname || ""}`.trim();
        }
        return income.customerName || "";
    }, []);

    const resolveIncomeDisplayName = useCallback((income) => {
        const invoiceNumber = (income?.invoiceNumber || "").trim();
        if (invoiceNumber) return invoiceNumber;
        const fallback = income?.originalFilename || "";
        return fallback || "Einnahme";
    }, []);

    const buildIncomeSearchValue = useCallback((income) => {
        const displayName = resolveIncomeDisplayName(income);
        const fileName = income?.originalFilename || "";
        const customerName = resolveIncomeCustomerName(income);
        return `${displayName} ${fileName} ${customerName}`.toLowerCase();
    }, [resolveIncomeCustomerName, resolveIncomeDisplayName]);

    const filterAndSortIncomes = useCallback((items) => {
        if (!Array.isArray(items)) return [];
        const q = query.trim().toLowerCase();
        let list = items;
        if (q) {
            list = list.filter((income) => buildIncomeSearchValue(income).includes(q));
        }
        const byDate = (a, b) => {
            const dateA = resolveIncomeDate(a);
            const dateB = resolveIncomeDate(b);
            const tsA = dateA ? new Date(dateA).getTime() : 0;
            const tsB = dateB ? new Date(dateB).getTime() : 0;
            return tsA - tsB;
        };
        const byName = (a, b) =>
            resolveIncomeDisplayName(a).localeCompare(resolveIncomeDisplayName(b), "de");
        switch (sortBy) {
            case "date-asc":
                return [...list].sort(byDate);
            case "name-asc":
                return [...list].sort(byName);
            case "name-desc":
                return [...list].sort((a, b) => -byName(a, b));
            case "date-desc":
            default:
                return [...list].sort((a, b) => -byDate(a, b));
        }
    }, [buildIncomeSearchValue, query, resolveIncomeDisplayName, sortBy]);

    const receiptFileName = receiptIncome?.originalFilename || "";
    const receiptContentType = String(receiptPreview?.type || "").toLowerCase();
    const isReceiptPdf = receiptContentType.includes("pdf") || receiptFileName.toLowerCase().endsWith(".pdf");
    const isReceiptImage =
        receiptContentType.startsWith("image/") || /\.(png|jpe?g|gif|bmp|webp)$/i.test(receiptFileName);

    const groupedIncomeMonths = useMemo(() => {
        const groups = {};
        if (incomeMonthIndexLoaded) {
            const months = Array.isArray(incomeMonthIndex) ? incomeMonthIndex : [];
            months.forEach(({ year, month }) => {
                const yearKey = String(year);
                const monthKey = String(month);
                if (!groups[yearKey]) groups[yearKey] = [];
                if (!groups[yearKey].includes(monthKey)) {
                    groups[yearKey].push(monthKey);
                }
            });
        }

        Object.keys(groups).forEach((yearKey) => {
            groups[yearKey].sort((a, b) => Number(b) - Number(a));
        });

        return groups;
    }, [incomeMonthIndex, incomeMonthIndexLoaded]);

    const sortedYears = useMemo(() => {
        return Object.keys(groupedIncomeMonths).sort((a, b) => Number(b) - Number(a));
    }, [groupedIncomeMonths]);

    const hasIncomeMonths = incomeMonthIndexLoaded && incomeMonthIndex.length > 0;

    useEffect(() => {
        if (typeof window === "undefined") return;
        try {
            sessionStorage.setItem(incomeGroupStateKey, JSON.stringify(expandedIncomeGroups));
        } catch {
            // ignore
        }
    }, [expandedIncomeGroups]);

    // Öffnen (Details + Editierbar)
    const openDetails = (income) => {
        setDetailIncome(income || null);
        // Bearbeitbares Modell erstellen (nur die Felder, die wir pflegen)
        setEditIncome({
            id: income?.id,
            invoiceDate: toDateInputValue(income?.invoiceDate),
            invoiceNumber: income?.invoiceNumber || "",
            customerId: income?.customer?.id || "",
            customerName: income?.customer
                ? `${income.customer.name || ""} ${income.customer.lastname || ""}`.trim()
                : income?.customerName || "",
            grossAmount: isFiniteNumber(income?.grossAmount) ? Number(income.grossAmount) : "",
            netAmount: isFiniteNumber(income?.netAmount) ? Number(income.netAmount) : "",
            vatAmount: isFiniteNumber(income?.vatAmount) ? Number(income.vatAmount) : "",
            description: income?.description || "",
            taxExcludedContribution: Boolean(income?.taxExcludedContribution),
            isEuService: Boolean(income?.isEuService),
        });
        setEditErrors({});
        setDetailOpen(true);
    };
    const closeDetails = () => {
        setDetailOpen(false);
        setDetailIncome(null);
        setEditIncome(null);
        setEditErrors({});
    };

    const updateEditIncome = useCallback((patch, changedField) => {
        setEditIncome((prev) => {
            const base = prev || {
                taxExcludedContribution: false,
                isEuService: false,
                customerId: "",
                customerName: "",
            };
            return applyEuCalculation(base, patch, changedField, toNumOrEmpty);
        });
    }, []);

    const validateEuService = (data, setErrors) => {
        if (!data?.isEuService) {
            return true;
        }
        const net = resolveEuNetAmount(data);
        if (!Number.isFinite(net) || net === 0) {
            if (setErrors) {
                setErrors((prev) => ({
                    ...(prev || {}),
                    netAmount: "Nettobetrag darf bei EU-Leistung nicht 0 sein.",
                }));
            }
            setToast({ open: true, msg: "Nettobetrag fuer EU-Leistung darf nicht 0 sein.", severity: "error" });
            return false;
        }
        return true;
    };

    const saveDetails = async () => {
        if (!editIncome?.id) return;
        setEditErrors({});
        if (!validateEuService(editIncome, setEditErrors)) return;
        setDetailSaving(true);
        try {
            // Payload bauen – nur die Felder senden, die dein Backend erwartet
            const payload = {
                invoiceDate: emptyToNull(editIncome.invoiceDate),
                invoiceNumber: emptyToNull(editIncome.invoiceNumber),
                customerName: emptyToNull(editIncome.customerName),
                grossAmount: parseOrNull(editIncome.grossAmount),
                netAmount: parseOrNull(editIncome.netAmount),
                vatAmount: parseOrNull(editIncome.vatAmount),
                description: emptyToNull(editIncome.description),
                taxExcludedContribution: Boolean(editIncome.taxExcludedContribution),
                isEuService: Boolean(editIncome.isEuService),
            };
            await updateIncome(editIncome.id, payload);
            await refresh({ silent: true });
            setToast({ open: true, msg: "Einnahme aktualisiert.", severity: "success" });
            closeDetails();
        } catch (e) {
            logError("Einnahme-Update fehlgeschlagen", e, { component: "IncomeManagement" });
            const backendError = e?.response?.data;
            const details = Array.isArray(backendError?.details) ? backendError.details : null;
            if (details) {
                applyIncomeErrors(details, setEditErrors);
            }
            setToast({ open: true, msg: "Aktualisierung fehlgeschlagen.", severity: "error" });
        } finally {
            setDetailSaving(false);
        }
    };


    const handleCopyIncome = async (income) => {
        if (!income) return;
        const customerName = income?.customer
            ? `${income.customer.name || ""} ${income.customer.lastname || ""}`.trim()
            : income?.customerName || null;
        const payload = {
            originalFilename: income?.originalFilename ?? null,
            invoiceNumber: income?.invoiceNumber ?? null,
            invoiceDate: null,
            customerName: customerName || null,
            grossAmount: parseOrNull(income?.grossAmount),
            netAmount: parseOrNull(income?.netAmount),
            vatAmount: parseOrNull(income?.vatAmount),
            extractedText: income?.extractedText ?? null,
            taxExcludedContribution: Boolean(income?.taxExcludedContribution),
            isEuService: Boolean(income?.isEuService),
        };
        setCopyingId(income?.id ?? null);
        try {
            await createIncome(payload);
            await refresh({ silent: true });
            setToast({ open: true, msg: "Einnahme kopiert.", severity: "success" });
        } catch (e) {
            logError("Fehler beim Kopieren der Einnahme", e, { component: "IncomeManagement" });
            setToast({ open: true, msg: "Kopieren fehlgeschlagen.", severity: "error" });
        } finally {
            setCopyingId(null);
        }
    };

    // Beleg anzeigen (nur wenn vorhanden)
    const hasReceipt = (income) => Boolean(income?.fileId || income?.originalFilename); // ggf. an dein Modell anpassen
    const closeReceipt = () => {
        setReceiptOpen(false);
        setReceiptIncome(null);
        setReceiptPreview(null);
        setReceiptError(null);
        setReceiptLoading(false);
    };
    const openReceipt = async (income) => {
        if (!income?.id) return;
        setReceiptOpen(true);
        setReceiptIncome(income);
        setReceiptError(null);
        setReceiptLoading(true);
        setReceiptPreview(null);
        try {
            const res = await getIncomeFile(income.id);
            const blob = res.data;
            if (!blob || blob.size === 0) {
                throw new Error("Beleg ist leer");
            }
            const url = URL.createObjectURL(blob);
            setReceiptPreview({ url, type: blob.type || "", size: blob.size });
        } catch (e) {
            logError("Beleg laden fehlgeschlagen", e, { component: "IncomeManagement" });
            setReceiptError("Beleg konnte nicht geladen werden.");
            setToast({ open: true, msg: "Beleg konnte nicht geladen werden.", severity: "error" });
        } finally {
            setReceiptLoading(false);
        }
    };

    useEffect(() => {
        return () => {
            if (receiptPreview?.url) {
                URL.revokeObjectURL(receiptPreview.url);
            }
        };
    }, [receiptPreview]);

    // Drag & Drop Events (Dialog)
    useEffect(() => {
        if (!hasAccounting) return;
        const el = dropRef.current;
        if (!el || manual) return;
        const prevent = (e) => {
            e.preventDefault();
            e.stopPropagation();
        };
        const onDrop = (e) => {
            prevent(e);
            const f = e.dataTransfer?.files?.[0];
            if (f) setFile(f);
        };
        const onOver = (e) => {
            prevent(e);
            el.classList.add("drag-active");
        };
        const onLeave = (e) => {
            prevent(e);
            el.classList.remove("drag-active");
        };
        el.addEventListener("dragenter", onOver);
        el.addEventListener("dragover", onOver);
        el.addEventListener("dragleave", onLeave);
        el.addEventListener("drop", onDrop);
        return () => {
            el.removeEventListener("dragenter", onOver);
            el.removeEventListener("dragover", onOver);
            el.removeEventListener("dragleave", onLeave);
            el.removeEventListener("drop", onDrop);
        };
    }, [open, manual, hasAccounting]);

    useEffect(() => {
        if (!file) {
            setPreviewUrl(null);
            setPreviewVisible(false);
            return;
        }
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    if (!hasAccounting) {
        return <IncomeManagementUpgradeNotice />;
    }

    return (
        <>
            <IncomeManagementHeader onRefresh={refresh} onOpenUpload={handleOpenUpload} />

            <RecurringIncomeSection
                recurringLoading={recurringLoading}
                recurringError={recurringError}
                recurringIncomes={recurringIncomes}
                onCreate={handleOpenRecurringCreate}
                onEdit={handleOpenRecurringEdit}
                onDelete={handleDeleteRecurring}
                recurringDeleteId={recurringDeleteId}
                recurrenceLabel={recurrenceLabel}
                formatDate={formatDate}
                formatDateOrDash={formatDateOrDash}
                fmtEUR={fmtEUR}
            />

            <IncomeToolbar
                query={query}
                onQueryChange={setQuery}
                sortBy={sortBy}
                onSortByChange={setSortBy}
            />

            <IncomeMonthsList
                loading={loading}
                err={err}
                incomeMonthIndexLoaded={incomeMonthIndexLoaded}
                hasIncomeMonths={hasIncomeMonths}
                sortedYears={sortedYears}
                groupedIncomeMonths={groupedIncomeMonths}
                expandedIncomeGroups={expandedIncomeGroups}
                loadingIncomeMonths={loadingIncomeMonths}
                loadedIncomeMonths={loadedIncomeMonths}
                incomeMonthData={incomeMonthData}
                buildMonthKey={buildMonthKey}
                filterAndSortIncomes={filterAndSortIncomes}
                resolveIncomeDate={resolveIncomeDate}
                resolveIncomeCustomerName={resolveIncomeCustomerName}
                resolveIncomeDisplayName={resolveIncomeDisplayName}
                fmtEUR={fmtEUR}
                onToggleYear={toggleYearExpanded}
                onToggleMonth={toggleMonthExpanded}
                onOpenDetails={openDetails}
                onCopyIncome={handleCopyIncome}
                onOpenReceipt={openReceipt}
                onOpenUpload={handleOpenUpload}
                hasReceipt={hasReceipt}
                copyingId={copyingId}
            />

            <RecurringIncomeDialog
                open={recurringDialogOpen}
                onClose={handleCloseRecurringDialog}
                onSave={handleSaveRecurring}
                recurringSaving={recurringSaving}
                recurringMode={recurringMode}
                recurringForm={recurringForm}
                recurringErrors={recurringErrors}
                customerOptions={customerOptions}
                loadingCustomers={loadingCustomers}
                recurringCustomerOption={recurringCustomerOption}
                updateRecurringForm={updateRecurringForm}
                recurrenceOptions={recurrenceOptions}
            />

            <IncomeUploadDialog
                open={open}
                onClose={handleCloseUpload}
                manual={manual}
                onManualToggle={handleManualToggle}
                dropRef={dropRef}
                fileInputRef={fileInputRef}
                file={file}
                onFileChange={setFile}
                previewVisible={previewVisible}
                onPreviewToggle={() => setPreviewVisible((prev) => !prev)}
                previewUrl={previewUrl}
                isPreviewPdf={isPreviewPdf}
                isPreviewImage={isPreviewImage}
                uploading={uploading}
                saving={saving}
                incomeData={incomeData}
                incomeErrors={incomeErrors}
                onUpdateIncomeData={updateIncomeData}
                onClearIncomeError={clearIncomeError}
                customerOptions={customerOptions}
                loadingCustomers={loadingCustomers}
                incomeCustomerOption={incomeCustomerOption}
                onSave={handleSave}
                onUpload={handleUpload}
                formatBytes={formatBytes}
                extOf={extOf}
            />

            <IncomeDetailsDialog
                open={detailOpen}
                onClose={closeDetails}
                onSave={saveDetails}
                detailSaving={detailSaving}
                detailIncome={detailIncome}
                editIncome={editIncome}
                editErrors={editErrors}
                onUpdateEditIncome={updateEditIncome}
                onSetEditIncome={setEditIncome}
                onClearEditError={clearEditError}
                resolveEuNetAmount={resolveEuNetAmount}
                fmtEUR={fmtEUR}
                formatDate={formatDate}
                customerOptions={customerOptions}
                loadingCustomers={loadingCustomers}
                editCustomerOption={editCustomerOption}
            />

            <IncomeReceiptDialog
                open={receiptOpen}
                onClose={closeReceipt}
                receiptIncome={receiptIncome}
                receiptPreview={receiptPreview}
                receiptLoading={receiptLoading}
                receiptError={receiptError}
                isReceiptPdf={isReceiptPdf}
                isReceiptImage={isReceiptImage}
                formatBytes={formatBytes}
                resolveIncomeDisplayName={resolveIncomeDisplayName}
            />

            <Snackbar
                open={toast.open}
                autoHideDuration={3000}
                onClose={() => setToast((t) => ({ ...t, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert onClose={() => setToast((t) => ({ ...t, open: false }))} severity={toast.severity} variant="filled" sx={{ width: "100%" }}>
                    {toast.msg}
                </Alert>
            </Snackbar>
        </>
    );
}
